import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertTriangle, Clock, CircleDollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS_META = {
  paid:    { Icon: CheckCircle2,    color: "text-qenva-cat-financing",  ring: "ring-qenva-cat-financing/30",  bg: "bg-qenva-cat-financing/10" },
  unpaid:  { Icon: Clock,           color: "text-zinc-600",              ring: "ring-zinc-300",                bg: "bg-zinc-100" },
  partial: { Icon: CircleDollarSign, color: "text-qenva-cat-financials", ring: "ring-qenva-cat-financials/30", bg: "bg-qenva-cat-financials/10" },
  overdue: { Icon: AlertTriangle,   color: "text-qenva-cat-contract",    ring: "ring-qenva-cat-contract/30",   bg: "bg-qenva-cat-contract/10" },
};

/**
 * InvoicePaymentStatusPanel — section dedicated to mutating the
 * payment_status of an invoice from its analysis page.
 *
 * The component owns its own local state (selected value, pending,
 * resolved alerts) but bubbles up the post-mutation document via
 * `onUpdated()` so the parent can refresh the Cashflow card etc.
 */
export default function InvoicePaymentStatusPanel({
  docId,
  currentStatus,
  onUpdated,
}) {
  const { t } = useTranslation("invoices");
  const initial = (currentStatus || "unpaid").toLowerCase();
  const [selected, setSelected] = useState(initial);
  const [pending, setPending] = useState(false);

  const CURRENT_META = STATUS_META[initial] || STATUS_META.unpaid;
  const CurrentIcon = CURRENT_META.Icon;

  const submit = async () => {
    if (selected === initial) return;
    setPending(true);
    try {
      const r = await api.patch(
        `/documents/${docId}/invoice/payment-status`,
        { payment_status: selected },
      );
      const resolvedCount = r.data.resolved_alert_ids?.length || 0;
      toast.success(
        resolvedCount > 0
          ? `${t("payment_status.updated_title")} — ${t(
              "payment_status.updated_with_resolved",
              { count: resolvedCount },
            )}`
          : t("payment_status.updated_title"),
      );
      onUpdated?.(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`${t("payment_status.update_failed")} — ${msg}`);
    } finally {
      setPending(false);
    }
  };

  return (
    <section
      className="bg-white border border-zinc-200 rounded-xl p-6"
      data-testid="invoice-payment-status-panel"
    >
      <header className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-semibold text-base tracking-tight text-qenva-navy">
            {t("payment_status.section_title")}
          </h2>
          <p className="text-sm text-zinc-500 mt-0.5">{t("payment_status.section_subtitle")}</p>
        </div>
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ${CURRENT_META.ring} ${CURRENT_META.bg}`}>
          <CurrentIcon size={13} className={CURRENT_META.color} />
          <span className={`text-[12px] font-bold uppercase tracking-wider ${CURRENT_META.color}`}>
            {t(`payment_status.values.${initial}`, { defaultValue: initial })}
          </span>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {["unpaid", "partial", "paid", "overdue"].map((v) => {
          const m = STATUS_META[v];
          const Icon = m.Icon;
          const isActive = selected === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => setSelected(v)}
              disabled={pending}
              data-testid={`invoice-payment-status-option-${v}`}
              className={`px-3 py-3 rounded-xl border text-left transition-all ${
                isActive
                  ? `border-qenva-navy ring-2 ring-qenva-navy/20 ${m.bg}`
                  : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Icon size={15} className={isActive ? m.color : "text-zinc-500"} />
              <p className={`mt-1.5 text-[12.5px] font-bold ${isActive ? "text-qenva-navy" : "text-zinc-700"}`}>
                {t(`payment_status.values.${v}`)}
              </p>
            </button>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={selected === initial || pending}
          data-testid="invoice-payment-status-confirm"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-white bg-qenva-navy hover:bg-[#13325A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {pending ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              {t("payment_status.updating")}
            </>
          ) : (
            t("payment_status.confirm")
          )}
        </button>
      </div>
    </section>
  );
}
